/**
 * Shared transform math for annotation wrappers. Both
 * `MarkInteractiveView` (stickers / stamps / emoji reactions) and
 * `DrawingAnnotationView` (drawings) emit the same shape of transform
 * string:
 *
 *   `translate(...) rotate(...) scale(sX, sY)`
 *
 * What differs is the SIZING MODEL of the wrapper that carries it:
 *
 *   - `intrinsic` — wrapper hugs its content. Used by marks: a sticker
 *     sizes to a 48×48 placeholder, a stamp sizes to its `max-content`
 *     text glyphs. The wrapper's CSS-layout box has no width/height
 *     overrides; the inner glyph dictates the box, and the transform
 *     `translate(-50%, -50%)` centers that box on `(centerX, centerY)`
 *     of the host. Scale is uniform (`scaleX === scaleY`).
 *
 *   - `container` — wrapper is sized to 100% × 100% of the
 *     `AnnotationViewContainer`. The transform's `scale(sX, sY)`
 *     shrinks the wrapper to the bbox fraction; the translate
 *     repositions the wrapper's center from the container's center
 *     `(0.5, 0.5)` to `(centerX, centerY)`. Used by drawings whose
 *     bbox is naturally a non-uniform fraction of the host (a wide
 *     squiggle has `frame.width = 0.6, frame.height = 0.2`). Scale
 *     can be non-uniform.
 *
 * Both paths set CSS variables `--annotation-inverse-scale-x` and
 * `--annotation-inverse-scale-y` whose values are the reciprocal of
 * the wrapper's scale on each axis. Any descendant that should NOT
 * scale with the wrapper (selection-chrome handles that need fixed
 * pixel size, the drawing's author avatar, the path's hit-test
 * stroke) reads these vars and applies `scale(1/sX, 1/sY)` to cancel
 * the wrapper's scale exactly. For marks the two axes are equal; for
 * drawings they're independent so a non-uniform-scaled drawing keeps
 * its chrome handles square.
 *
 * The `% translate` in the `container` branch is in the wrapper's
 * UNSCALED CSS-pixel coords — and because the wrapper is 100% × 100%
 * of the container, those CSS pixels equal container pixels. So
 * `translate((x - 0.5) * 100%, (y - 0.5) * 100%)` literally moves
 * the wrapper's center from `(containerW/2, containerH/2)` to
 * `(x * containerW, y * containerH)`, which is the desired bbox
 * center.
 *
 * Container-relative natural sizing
 * ---------------------------------
 *
 * The wrapper transform NO LONGER folds in `--annotation-natural-scale`.
 * Stream-vs-modal sizing is owned by `.annotations-container` itself
 * (in `annotation.scss`): the container's `transform: scale(natural)`
 * shrinks the entire annotation surface uniformly, so a stamp's
 * `scale(s)` here only carries the user's persisted resize intent.
 * Previously this helper composed `scale(s * var(--natural))` on
 * every wrapper; that's now redundant — the container handles it
 * once for every annotation type at once.
 *
 * Chrome handles that counter-scale still divide by
 * `--annotation-natural-scale` to compensate the container's scale —
 * see the SCSS rule on
 * `.annotation-view .annotation-selection-chrome__handle` in
 * `annotation.scss`. Without that compensation a 10px handle would
 * shrink to 5px in stream mode (where the container is at scale 0.5).
 */
// ---------------------------------------------------------------------------
// Math
// ---------------------------------------------------------------------------
// Floor on the scale magnitude before reciprocating. A degenerate
// (rendered-scale → 0) wrapper should not blow up to `1/0 = Infinity`
// in inverse-scale chrome — falling back to `1` paints chrome at its
// intrinsic size, which is harmless and recoverable on the next frame.
const SCALE_EPSILON = 0.0001;
function reciprocal(scale) {
    return Math.abs(scale) > SCALE_EPSILON ? 1 / scale : 1;
}
/**
 * Compute the inline style for an annotation wrapper. Sets `transform`,
 * positional offsets, and the inverse-scale CSS vars consumed by
 * non-scaling descendants.
 *
 * The returned object is *partial* — it intentionally omits
 * `position: absolute`, `transform-origin`, and the various per-type
 * visual rules (background, border, etc.). Those live in the per-type
 * SCSS so the inline style can stay focused on the per-frame transform
 * payload that React re-emits each render.
 */
export function annotationTransformStyle(t) {
    const inverseScaleX = reciprocal(t.scaleX);
    const inverseScaleY = reciprocal(t.scaleY);
    // Wrapper carries ONLY the persisted scale; container-relative
    // sizing is handled once at `.annotations-container` via its own
    // `transform: scale(--annotation-natural-scale)`.
    const transform = t.sizing === "intrinsic"
        ? `translate(-50%, -50%) rotate(${t.rotation}deg) scale(${t.scaleX}, ${t.scaleY})`
        : `translate(${(t.centerX - 0.5) * 100}%, ${(t.centerY - 0.5) * 100}%) rotate(${t.rotation}deg) scale(${t.scaleX}, ${t.scaleY})`;
    const inverseScaleVars = {
        ["--annotation-inverse-scale-x"]: String(inverseScaleX),
        ["--annotation-inverse-scale-y"]: String(inverseScaleY),
    };
    if (t.sizing === "intrinsic") {
        return {
            left: `${t.centerX * 100}%`,
            top: `${t.centerY * 100}%`,
            transform,
            ...inverseScaleVars,
        };
    }
    return {
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        transform,
        ...inverseScaleVars,
    };
}
